import React, { useState } from 'react';
import 'tailwindcss/tailwind.css';
import { ReactComponent as PageFirstIcon } from '../../../assets/icons/page-first.svg';
import { ReactComponent as PageLastIcon } from '../../../assets/icons/page-last.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../../assets/icons/chevron-right.svg';

const COMPACT_PAGE_LIMIT = 7;

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= COMPACT_PAGE_LIMIT) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'end-ellipsis', totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, 'start-ellipsis', ...Array.from({ length: 5 }, (_, index) => totalPages - 4 + index)];
  }

  return [
    1,
    'start-ellipsis',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'end-ellipsis',
    totalPages,
  ];
};

const Pagination = ({ total, pageSize, onPageChange, compact = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const totalPages = Math.ceil(total / pageSize);
  const pageItems = compact
    ? getPageItems(currentPage, totalPages)
    : Array.from({ length: totalPages }, (_, index) => index + 1);

  const changePage = page => {
    setCurrentPage(page);
    setPageInput(String(page));
    onPageChange(page);
  };

  const handleFirstPage = () => {
    changePage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      changePage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      changePage(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    changePage(totalPages);
  };

  const handlePageClick = (page) => {
    changePage(page);
  };

  const handlePageInputChange = (e) => {
    const { value } = e.target;

    if (value === '' || /^\d+$/.test(value)) {
      setPageInput(value);
    }
  };

  const handlePageInputCommit = () => {
    const page = Number(pageInput);

    if (Number.isInteger(page) && page >= 1 && page <= totalPages) {
      if (page !== currentPage) {
        changePage(page);
      } else {
        setPageInput(String(page));
      }
    } else {
      setPageInput(String(currentPage));
    }
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handlePageInputCommit();
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <span
        onClick={handleFirstPage}
        className={`cursor-pointer ${currentPage === 1 ? 'text-gray-400' : 'text-gray-600'}`}
      >
        <PageFirstIcon className="w-5 h-5" style={{ fill: '#999999' }} />
      </span>
      <span
        onClick={handlePreviousPage}
        className={`cursor-pointer ${currentPage === 1 ? 'text-gray-400' : 'text-gray-600'}`}
      >
        <ChevronLeftIcon className="w-5 h-5" style={{ fill: '#999999' }} />
      </span>
      {pageItems.map(item => (
        typeof item === 'number' ? (
          <span
            key={item}
            onClick={() => handlePageClick(item)}
            className={`px-1 cursor-pointer font-semibold ${currentPage === item ? 'text-[#818cf8]' : 'text-black'}`}
          >
            {item}
          </span>
        ) : (
          <span key={item} className="px-1 font-semibold text-black">
            ...
          </span>
        )
      ))}
      <span
        onClick={handleNextPage}
        className={`cursor-pointer ${currentPage === totalPages ? 'text-gray-400' : 'text-gray-600'}`}
      >
        <ChevronRightIcon className="w-5 h-5" style={{ fill: '#999999' }} />
      </span>
      <span
        onClick={handleLastPage}
        className={`cursor-pointer ${currentPage === totalPages ? 'text-gray-400' : 'text-gray-600'}`}
      >
        <PageLastIcon className="w-5 h-5" style={{ fill: '#999999' }} />
      </span>
      <span className="!ml-12 text-gray-500">Go to</span>
      <input
        type="text"
        inputMode="numeric"
        aria-label="Go to page"
        value={pageInput}
        onChange={handlePageInputChange}
        onBlur={handlePageInputCommit}
        onKeyDown={handlePageInputKeyDown}
        className="w-16 p-1 ml-2 bg-gray-200 border rounded"
      />
    </div>
  );
};

export default Pagination;
